import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, DollarSign, Calendar, Users, Trash2 } from 'lucide-react';

const CompletedProjectCard = ({ project, userRole, onPaymentUpdate, onDelete }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate('/project-details', {
      state: {
        projectId: project?.id
      }
    });
  };

  const handlePaymentToggle = async (e) => {
    e?.stopPropagation();
    if (onPaymentUpdate) {
      await onPaymentUpdate(project?.id, !project?.isPaid);
    }
  };

  const handleDelete = async (e) => {
    e?.stopPropagation();
    
    if (window.confirm(`Are you sure you want to delete "${project?.name}"? This action cannot be undone.`)) {
      try {
        await onDelete?.(project?.id);
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Failed to delete project. Please try again.');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 cursor-pointer p-6 border-l-4 border-green-500"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{project?.name}</h3>
          <div className="flex items-center text-green-600 text-sm font-medium">
            <CheckCircle className="w-4 h-4 mr-1" />
            <span>Completed</span>
          </div>
        </div>
      </div>

      {/* Project Details */}
      <div className="space-y-3 mb-4">
        {/* Date Range */}
        <div className="flex items-center text-sm text-gray-600">
          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
          <span>{formatDate(project?.startDate)} - {formatDate(project?.endDate)}</span>
        </div>

        {/* Team Members */}
        {project?.teamMembers && project?.teamMembers?.length > 0 && (
          <div className="flex items-center text-sm text-gray-600">
            <Users className="w-4 h-4 mr-2 text-gray-400" />
            <span>{project?.teamMembers?.length} team member{project?.teamMembers?.length !== 1 ? 's' : ''}</span>
          </div>
        )}

        {/* Team Avatars */}
        {project?.teamMembers && project?.teamMembers?.length > 0 && (
          <div className="flex -space-x-2">
            {project?.teamMembers?.slice(0, 4)?.map((member, index) => (
              <div
                key={index}
                className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center overflow-hidden"
                title={member?.name}
              >
                {member?.avatar ? (
                  <img
                    src={member?.avatar}
                    alt={member?.avatarAlt || `Avatar of ${member?.name}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-xs font-medium text-gray-600">
                    {member?.name?.charAt(0)?.toUpperCase()}
                  </span>
                )}
              </div>
            ))}
            {project?.teamMembers?.length > 4 && (
              <div className="w-8 h-8 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center">
                <span className="text-xs font-medium text-gray-600">
                  +{project?.teamMembers?.length - 4}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Payment Status */}
      {userRole === 'boss' && (
        <div className="pt-4 border-t border-gray-200 space-y-2">
          <button
            onClick={handlePaymentToggle}
            className={`w-full flex items-center justify-center px-4 py-2 rounded-lg font-medium transition-colors ${
              project?.isPaid
                ? 'bg-green-100 text-green-700 hover:bg-green-200' :'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
            }`}
          >
            <DollarSign className="w-4 h-4 mr-2" />
            {project?.isPaid ? 'Paid' : 'Mark as Paid'}
          </button>
          <button
            onClick={handleDelete}
            className="w-full flex items-center justify-center px-4 py-2 rounded-lg font-medium transition-colors bg-red-50 text-red-700 hover:bg-red-100"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Project
          </button>
        </div>
      )}
    </div>
  );
};

export default CompletedProjectCard;